/**
 * The ledger's filter state — one shape, read from and written to the address
 * bar and nowhere else.
 *
 * **The URL is the only store.** A filter held in component state is a view
 * nobody can send to a colleague, a reload throws the operator back to the
 * unfiltered ledger, and Back leaves the surface instead of undoing the last
 * narrowing. The engineering standard states it outright for admin tables, and
 * the ordinal beside these facets already lives here.
 *
 * **Every facet resets the ordinal.** Page four of «كلّ الخطط» is not page four
 * of one plan, and carrying the number across answers a question nobody asked
 * with rows from a list that no longer exists.
 */

/** The «no narrowing» member of a select. A UI value; it never reaches the wire. */
export const ALL = 'all';

export interface LedgerFilters {
  /** The operator's term, resolved against the account register — never sent to the money service. */
  term: string;
  /** A plan id, or `null` for every plan. */
  planId: string | null;
  /** A subscription-lifecycle state, or `null` for every state. */
  state: string | null;
  /** An entitlement source, or `null` for both. */
  source: string | null;
}

export const EMPTY_FILTERS: LedgerFilters = { term: '', planId: null, state: null, source: null };

/** Read the four facets off an address. Absent and empty are the same thing. */
export function readFilters(params: URLSearchParams): LedgerFilters {
  return {
    term: params.get('q')?.trim() ?? '',
    planId: params.get('plan') || null,
    state: params.get('state') || null,
    source: params.get('source') || null,
  };
}

/**
 * Write the four facets back onto an address, dropping the ordinal.
 *
 * The parameter is **removed** rather than written empty when the facet is
 * cleared: one list has one address, and `?q=&plan=` is a second spelling of
 * the unfiltered ledger that no link would ever match.
 */
export function writeFilters(params: URLSearchParams, filters: LedgerFilters): URLSearchParams {
  const next = new URLSearchParams(params);

  setOrDelete(next, 'q', filters.term.trim());
  setOrDelete(next, 'plan', filters.planId);
  setOrDelete(next, 'state', filters.state);
  setOrDelete(next, 'source', filters.source);
  next.delete('page');

  return next;
}

/** True when at least one facet narrows the ledger — what the «clear» control keys off. */
export function hasAnyFilter(filters: LedgerFilters): boolean {
  return Boolean(filters.term.trim() || filters.planId || filters.state || filters.source);
}

function setOrDelete(params: URLSearchParams, name: string, value: string | null) {
  if (value) params.set(name, value);
  else params.delete(name);
}
